import moment from 'moment';
import PhoneLineIcon from '../../atoms/icons/PhoneLineIcon';
import IconWithText from '../../atoms/typography/IconWithText';
import Text from '../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import styles from './CandidatesRow.module.css';
import { MouseEvent, useEffect, useState } from 'react';
import { CheckboxInput } from '../../molecules/checkbox-input';
import CandidateSituationProfile from '../side-panel/homeseeker-application-side-panel/situation-profile/CandidateSituationProfile';
import { HomeseekerApplication } from '../../../api/homeseeker-application/findHomeSeekerApplicationById';
import CandidateTableButtonGroup from '../button-group/CandidateTableButtonGroup';
import CandidateViewingStatus from '../../../enums/candidateViewingStatus';
import Notifications from '../notifications/Notifications';
import { useTranslation } from 'react-i18next';
import ApproveHomeseekerApplication from '../../../api/homeseeker/status/ApproveHomeseekerApplication';
import RejectHomeseekerApplication from '../../../api/homeseeker/status/RejectHomeseekerApplication';
import { Badge } from '../../molecules/badge';
import CandidateStatus from '../../../enums/candidateStatus';
import { classNames } from '../../utils';
import { moveHomeseekersToWaitingList } from '../../../redux/actions/candidates/setCandidateStatus';
import SendMessageModal from '../send-message-modal/SendMessageModal';
import { useNavigate } from 'react-router-dom';
import { BulkActionDropdownCustomEventType } from '../bulk-action-dropdown/BulkActionEventManager';
import BulkActionDropdownNew from '../bulk-action-dropdown/BulkActionDropdownNew';
import UserStarLineIcon from '../../atoms/icons/UserStarLineIcon';
import { ContactRequestListingResponse } from '../../../api/transactional-listing/findListingById';
import HomeseekerApplicationSourceIcon from '../../molecules/homeseeker-application-source-icon/HomeseekerApplicationSourceIcon';
import { renderGender } from '../../../utils/renders';
import assignTenantToListing from '../../../api/transactional-listing/assignTenantToListing';
import UnassignTenantModal from '../../../components/modals/unassignTenantModal/UnassignTenantModal';
import InviteToViewingModalNew from '../invite-to-viewing/InviteToViewingModalNew';

interface CandidatesRowProps {
    candidate: HomeseekerApplication;
    transactionalListingId: string;
    transactionalListing: ContactRequestListingResponse;
}

const CandidatesRow = ({
    candidate,
    transactionalListingId,
    transactionalListing
}: CandidatesRowProps) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const [isCandidateSelected, setIsCandidateSelected] = useState(false);

    const handleBulkActionEvent = (e: BulkActionDropdownCustomEventType) => {
        if (e.detail == null) {
            return;
        }

        const { shouldSelectAll } = e.detail;
        setIsCandidateSelected(shouldSelectAll);

        if (shouldSelectAll) {
            BulkActionDropdownNew.store(candidate.id);
        } else {
            BulkActionDropdownNew.remove(candidate.id);
        }
    };

    useEffect(() => {
        BulkActionDropdownNew.addListener(handleBulkActionEvent);

        return () => {
            BulkActionDropdownNew.removeListener(handleBulkActionEvent);
            BulkActionDropdownNew.setShouldSelectAll(false);
            BulkActionDropdownNew.remove(candidate.id);
        }
    }, [])


    const renderDate = (date) => {
        const realDate = moment(date);

        if (!realDate.isValid()) {
            return '-';
        }

        return realDate.fromNow();
    }

    const onUnassignTenant = (e: MouseEvent) => {
        e.stopPropagation();

        UnassignTenantModal.open(transactionalListingId, candidate.id, candidate.fullName);
    }

    const onApprove = (e: MouseEvent) => {
        e.stopPropagation();

        try {
            ApproveHomeseekerApplication({
                homeseekerApplicationIds: [candidate.id],
            });

            Notifications.add({
                type: 'success',
                message: t('statusbuttongroup.toast.success')
            });
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        }
    }

    const onMoveToWaitingList = (e: MouseEvent) => {
        e.stopPropagation();

        try {
            moveHomeseekersToWaitingList({
                homeseekerApplicationIds: [candidate.id],
            });

            Notifications.add({
                type: 'success',
                message: t('statusbuttongroup.toast.success')
            });
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        }
    }

    const onRejectWithMessage = (e: MouseEvent) => {
        e.stopPropagation();

        try {
            RejectHomeseekerApplication({
                homeseekerApplicationIds: [candidate.id],
                transactionalListingId,
                notifyHomeseeker: true
            });

            Notifications.add({
                type: 'success',
                message: t('statusbuttongroup.toast.success')
            });
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        }
    }

    const onRejectWithoutMessage = (e: MouseEvent) => {
        e.stopPropagation();

        try {
            RejectHomeseekerApplication({
                homeseekerApplicationIds: [candidate.id],
                transactionalListingId,
                notifyHomeseeker: false
            });

            Notifications.add({
                type: 'success',
                message: t('statusbuttongroup.toast.success')
            });
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        }
    }

    const onAssignTenant = (e: MouseEvent) => {
        e.stopPropagation();

        try {
            assignTenantToListing({
                homeseekerApplicationId: candidate.id,
                shouldArchiveListing: false
            });

            Notifications.add({
                type: 'success',
                message: t('statusbuttongroup.toast.success')
            });
        } catch (e) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        }
    }

    const onSendMessage = (e: MouseEvent) => {
        e.stopPropagation();

        SendMessageModal.open(candidate.id);
    }

    const renderAssessmentBadge = () => {
        const hasFilledExtraInfo = candidate.hasAcceptedAgentPrivacyPolicy;

        if (hasFilledExtraInfo) {
            return <Badge className={classNames(styles.badge, styles.assessment, styles.hasInfo)} label={t('candidate.status.extraInfo.sent')} />
        }

        return <Badge className={classNames(styles.badge, styles.assessment, styles.noInfo)} label={t('candidate.status.extraInfo.requested')} />
    }

    const renderBadge = (candidate: HomeseekerApplication) => {
        if (candidate.isListingTenant) {
            return <Badge className={classNames(styles.badge, styles.tenant)} before={<UserStarLineIcon/>} label={t('candidate.status.tenant')}/>
        }

        switch (candidate.status) {
            case CandidateStatus.None:
                return <Badge className={classNames(styles.badge, styles.new)} label={t('candidate.status.none')} />
            case CandidateStatus.Assessment:
                return renderAssessmentBadge();
            case CandidateStatus.Rejected:
                return <Badge className={classNames(styles.badge, styles.rejected)} label={t('candidate.status.rejected')} />
            case CandidateStatus.NotSuitable:
                return <Badge className={classNames(styles.badge, styles.waitingList)} label={t('candidate.status.waitinglist')} />
            case CandidateStatus.Suitable:
                return <Badge className={classNames(styles.badge, styles.approved)} label={t('candidate.status.suitable')} />
        }
    }

    const handleCheckboxClick = (e: MouseEvent) => {
        e.stopPropagation();

        setIsCandidateSelected(!isCandidateSelected);
    }

    const handleRowClick = (e: MouseEvent<HTMLTableRowElement>) => {
        e.stopPropagation();

        navigate(`/dashboard/advertenties/${candidate.transactionalListingId}?status=${candidate.status}&selectedCandidateId=${candidate.id}`)
    }

    if (candidate === null) {
        return null;
    }

    return (
        <tr className={styles.CandidatesRow} onClick={handleRowClick}>
            <td className={styles.candidateInfoContainer}>
                <CheckboxInput
                    name='selectCandidate'
                    onClick={handleCheckboxClick}
                    checked={isCandidateSelected}
                />
                <div className={styles.candidateInfo}>
                    <div className={styles.candidateDetails}>
                        <div className={styles.homeseekerName}>
                            <HomeseekerApplicationSourceIcon source={candidate.source} />
                            <Text
                                className={styles.fullName}
                                color={ColorEnum.Primary700}
                                size={FontSizeEnum.Md}
                                weight={FontWeightEnum.Semibold}
                            >
                                {`${renderGender(candidate.gender) ?? ''} ${candidate.fullName}`}
                            </Text>
                        </div>
                        <IconWithText
                            icon={<PhoneLineIcon />}
                        >
                            {candidate.phone === '' ? '-' : candidate.phone}
                        </IconWithText>
                    </div>
                    {renderBadge(candidate)}
                </div>
            </td>
            <td>
                <Text
                    size={FontSizeEnum.Sm}
                    className={styles.nowrap}
                >
                    {renderDate(candidate.createdAt)}
                </Text>
            </td>
            <td>
                <CandidateSituationProfile
                    homeseeker={candidate}
                    noHeading
                />
            </td>
            <td>
                <CandidateTableButtonGroup
                    status={candidate.status}
                    hasCotenants={candidate.hasCoTenants}
                    hasScreeningOutcome={(candidate.screeningRequestOutcome !== '')}
                    isListingTenant={candidate.isListingTenant}
                    screeningRequestStatus={candidate.screeningRequestStatus}
                    hasCoTenantsWithInvitedScreeningRequests={candidate.hasCoTenantsWithInvitedScreeningRequests}
                    invitedToViewingPending={candidate.upcomingOrMostRecentViewing?.status === CandidateViewingStatus.Pending}
                    hasDeclinedInviteToViewing={candidate.upcomingOrMostRecentViewing?.status === CandidateViewingStatus.Declined}
                    hasAcceptedInviteToViewing={candidate.upcomingOrMostRecentViewing?.status === CandidateViewingStatus.Accepted}
                    hasFilledExtraInfo={candidate.hasAcceptedAgentPrivacyPolicy}
                    hasReceivedExtraInfo={candidate.extraInfoFormLastSentOn !== null}
                    onUnassignTenant={onUnassignTenant}
                    onRejectWithMessage={onRejectWithMessage}
                    onRejectWithoutMessage={onRejectWithoutMessage}
                    onMoveToWaitingList={onMoveToWaitingList}
                    onSendMessage={onSendMessage}
                    onInviteToViewing={(e: MouseEvent) => { e.stopPropagation(); InviteToViewingModalNew.open(transactionalListingId, candidate.id, candidate.fullName, transactionalListing.listing.city, transactionalListing.listing.street) }}
                    onAssignTenant={onAssignTenant}
                    onApprove={onApprove}
                />
            </td>
        </tr>
    );
}

export default CandidatesRow;
